import type { AccessorKeyColumnDef, TableOptions } from "@tanstack/react-table";

import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import React, { useMemo } from "react";

import { useIsRtl } from "../hooks/useIsRtl";

type DefaultT = { [key: string]: React.JSX.Element };

export type TableDesktopSkeletonLoaderProps = {
  columns: AccessorKeyColumnDef<any, any>[];
  pageSize?: number;
  /**
   * Overrides the skeleton row height (px) so it matches a real row taller
   * than the default — e.g. a source cell with an icon and two text lines.
   * Left unset, rows keep their existing height (padding + bar only).
   */
  rowHeight?: number;
  // Mirrors TableDesktop's fixedLayout so the skeleton's column widths match
  // the real table's once loading finishes.
  fixedLayout?: boolean;
  // Mirrors TableDesktop's scrollFrame — drops the skeleton's own outer
  // border/corner radii since the scroll viewport draws the frame instead.
  insideScrollFrame?: boolean;
} & Pick<TableOptions<any>, "filterFns">;

export const TableDesktopSkeletonLoader = ({
  columns,
  pageSize = 10,
  filterFns,
  rowHeight,
  fixedLayout = false,
  insideScrollFrame = false,
}: TableDesktopSkeletonLoaderProps) => {
  const isRtl = useIsRtl();
  const { defaultColumns, defaultData } = useMemo(() => {
    const defaultColumns = columns.map((column) => {
      const key = (column.accessorKey ?? column.id) as string;
      return {
        id: key,
        header: column.header,
        accessorKey: key,
        cell: (info) => info.getValue() as React.JSX.Element,
        columnType: "text",
        size: column.size,
      };
    }) as AccessorKeyColumnDef<DefaultT, any>[];

    const defaultData: DefaultT[] = Array.from({ length: pageSize }, (_) => {
      const row: DefaultT = {};
      defaultColumns.forEach((column) => {
        if (column.accessorKey) {
          row[column.accessorKey] = (
            <SkeletonLoader
              height={6}
              variant="rectangular"
              width={column.size}
            />
          );
        }
      });

      return row as DefaultT;
    });

    return { defaultColumns, defaultData };
  }, [columns, pageSize]);

  const table = useReactTable({
    data: defaultData,
    columns: defaultColumns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeDirection: isRtl ? "rtl" : "ltr",
    filterFns: filterFns,
  });
  const rows = table.getRowModel().rows;

  return (
    <div data-testid="table-desktop-skeleton-loader">
      <table
        className={clsx(
          "w-full border-separate border-spacing-0",
          fixedLayout && "table-fixed",
        )}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-surface-foreground">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={clsx(
                    "relative px-3 py-2 text-start text-text-secondary",
                    !insideScrollFrame &&
                      "border-t border-border-primary-base first:rounded-ss-lg first:border-s last:rounded-se-lg last:border-e",
                  )}
                  colSpan={header.colSpan}
                  style={
                    fixedLayout
                      ? { width: header.getSize() }
                      : { minWidth: header.getSize() }
                  }
                >
                  <div className="flex items-center justify-between">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    {/* Reserves the real header's sort-button height (h-8) so
                    the skeleton doesn't render shorter and jump on load. */}
                    <div aria-hidden="true" className="h-8 w-0 shrink-0" />
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id}
              className="hover:bg-background-table-hover"
              style={rowHeight ? { height: rowHeight } : undefined}
              data-testid="skeleton-row"
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={clsx(
                    "border-b border-border-primary-base p-3 text-grey-900",
                    !insideScrollFrame && "first:border-s last:border-e",
                    !insideScrollFrame &&
                      index === table.getRowCount() - 1 &&
                      "first:rounded-bl-lg last:rounded-br-lg",
                  )}
                  style={
                    fixedLayout
                      ? { width: cell.column.getSize() }
                      : { minWidth: cell.column.getSize() }
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
